import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Badge } from '@anyui/core';
import { useUserRoles } from '@anytag/auth';
import { useQueryHelper } from '@anytag/shared-hooks';
import { Form, Button, Label } from '@anytag/shared-atoms';
import { SelectorField, TextField } from '@anytag/shared-molecules';
import {
  namedOperations,
  useSendBcaRequestMutation,
  useRequestedBcaBrandsQuery,
  useReSendBcaRequestMutation,
  useSelectableBcaBrandsQuery
} from '@anytag/graphql';
import { InfluencerFormValues } from '../../schema-types';
import { EMPTY_BRAND_CONTENT_FIELD, getBrandContentPermissionsStatusInfo } from './utils';

export const BrandContentPermissionsSection = () => {
  const { isAdminStaff } = useUserRoles();
  const { enqueueSnackbar, t } = useQueryHelper();
  const { control, watch } = useFormContext<InfluencerFormValues>();
  const { fields, append, replace } = useFieldArray<InfluencerFormValues, 'brandContentPermission'>({
    name: 'brandContentPermission',
    control
  });

  const [influencerId, instagramAccounts] = watch(['id', 'instagramAccounts']);
  const lastFieldArrayIndex = (fields?.length || 1) - 1;
  const brandContentPermissionValue = watch('brandContentPermission');

  const { data: requestedData, loading: requestedLoading } = useRequestedBcaBrandsQuery({
    skip: !influencerId,
    variables: { influencerId: Number(influencerId) },
    onCompleted: ({ requestedBcaBrands }) => {
      replace(
        requestedBcaBrands?.map((requestedBcaBrand) => ({
          instagram_account_id: requestedBcaBrand.instagramAccount.id.toString(),
          instagram_account_name: requestedBcaBrand.instagramAccount.name,
          brand_id: requestedBcaBrand.brand.id.toString(),
          status: requestedBcaBrand.status,
          isResendable: requestedBcaBrand.isResendable,
          // disable for all data that get from requestedBcaBrands API
          // https://adasiaholdings.slack.com/archives/C02C5J5B7KL/p1692780907914519
          disabled: true
        })) || []
      );
    }
  });
  const { data } = useSelectableBcaBrandsQuery({
    skip: !influencerId,
    variables: { influencerId: Number(influencerId) },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });
  const { callSendBcaRequest, loading: sendLoading } = useSendBcaRequestMutation({
    refetchQueries: [namedOperations.Query.RequestedBcaBrands]
  });
  const { callReSendBcaRequest, loading: reSendLoading } = useReSendBcaRequestMutation({
    refetchQueries: [namedOperations.Query.RequestedBcaBrands]
  });

  useEffect(() => {
    /**
     * we need to wait and see if this account doesn't have any requestedBcaBrands
     * then if there is just 1 selectableBcaBrands?.instagramAccounts - we need to pre-fill "Instagram Account" field and use TextFormField instead of Selector
     * if we have more IG accounts and no pre-selected data - we need to create an empty fieldArray entry
     *  */
    if (requestedData?.requestedBcaBrands?.length === 0 && brandContentPermissionValue?.length === 0) {
      if (data?.selectableBcaBrands?.instagramAccounts.length === 1) {
        append(
          {
            ...EMPTY_BRAND_CONTENT_FIELD,
            instagram_account_id: data.selectableBcaBrands.instagramAccounts.at(0)?.id.toString() || '',
            instagram_account_name: data.selectableBcaBrands.instagramAccounts.at(0)?.name || ''
          },
          { shouldFocus: false }
        );
      } else if ((data?.selectableBcaBrands?.instagramAccounts.length || 0) > 1) {
        append(EMPTY_BRAND_CONTENT_FIELD);
      }
    }
  }, [
    requestedData?.requestedBcaBrands?.length,
    data?.selectableBcaBrands?.instagramAccounts.length,
    brandContentPermissionValue?.length
  ]);

  if (!(isAdminStaff && influencerId && instagramAccounts.length)) return null;

  const handleSendPermissionRequest = async (fieldArrayIndex: number, isReSend?: boolean) => {
    try {
      if (brandContentPermissionValue?.[fieldArrayIndex]) {
        const variables = {
          input: {
            brandIgId: Number(brandContentPermissionValue.at(fieldArrayIndex)?.brand_id || ''),
            creatorIgId: Number(brandContentPermissionValue.at(fieldArrayIndex)?.instagram_account_id || '')
          }
        };
        if (isReSend) {
          await callReSendBcaRequest({ variables });
        } else {
          await callSendBcaRequest({ variables });
        }
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  const handleAddBrandContentFormField = () => {
    // in case of the only one instagramAccount from selectableBcaBrands - we must pre-fill it, because our BE relation in that case is one-to-many
    if (data?.selectableBcaBrands?.instagramAccounts.length === 1) {
      append(
        {
          ...EMPTY_BRAND_CONTENT_FIELD,
          instagram_account_id: data.selectableBcaBrands.instagramAccounts.at(0)?.id?.toString() || '',
          instagram_account_name: data.selectableBcaBrands.instagramAccounts.at(0)?.name || ''
        },
        { shouldFocus: false }
      );
    } else {
      append(EMPTY_BRAND_CONTENT_FIELD, { shouldFocus: false });
    }
  };

  const loading = requestedLoading || sendLoading || reSendLoading;

  return data?.selectableBcaBrands?.isBcaAvailable ? (
    <>
      <Form.SectionTitle className="mt-6 mb-0" label={t('Brand content permission request')} />
      {/* hidden due to https://adasiaholdings.atlassian.net/browse/AT-5028 */}
      {/* <Anchor variant="blue" target="_blank" css={{ whiteSpace: 'nowrap' }}>
          {t('Help page')}
        </Anchor> */}

      {fields.map((brandContentPermission, index) => (
        <div key={index}>
          {(data.selectableBcaBrands?.instagramAccounts.length || 0) > 1 ? (
            <SelectorField
              title={t('TextForm.Instagram Account')}
              name={`brandContentPermission.${index}.instagram_account_id`}
              options={data.selectableBcaBrands?.instagramAccounts.map((o) => ({
                value: o.id.toString(),
                label: o.name
              }))}
              className="mt-2"
              disabled={brandContentPermission.disabled || loading}
            />
          ) : (
            <TextField
              title={t('TextForm.Instagram Account')}
              name={`brandContentPermission.${index}.instagram_account_name`}
              disabled={brandContentPermission.disabled || loading}
              className="pointer-events-none"
              autoFocus={false}
            />
          )}
          <SelectorField
            name={`brandContentPermission.${index}.brand_id`}
            title={t('Selector.Brand')}
            options={data.selectableBcaBrands?.brands.map((o) => ({ value: o.id.toString(), label: o.name }))}
            disabled={brandContentPermission.disabled || loading}
          />
          {brandContentPermission?.status ? (
            <div className="mt-2 flex items-baseline">
              <Label title={t('Label.Status')} className="w-max" />
              <Badge className="ml-4" {...getBrandContentPermissionsStatusInfo(t, brandContentPermission.status)} />

              {brandContentPermission?.isResendable ? (
                <span
                  onClick={() => handleSendPermissionRequest(index, true)}
                  className="ml-auto text-base font-normal text-active"
                >
                  {t('Resend request')}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
      {brandContentPermissionValue?.[lastFieldArrayIndex]?.status === null ? (
        <Button
          title={t('Button.Send')}
          variant="solid"
          disabled={
            !brandContentPermissionValue?.at(lastFieldArrayIndex)?.instagram_account_id ||
            !brandContentPermissionValue?.at(lastFieldArrayIndex)?.brand_id ||
            loading
          }
          onClick={() => handleSendPermissionRequest(lastFieldArrayIndex)}
          className="mt-2 ml-auto flex min-w-20"
          loading={{ showIcon: true, status: sendLoading || reSendLoading, title: t('Button.Sending') }}
        />
      ) : (
        <Button
          title={t('Button.Add Brand')}
          variant="outline"
          onClick={handleAddBrandContentFormField}
          className="mt-2 ml-auto flex min-w-20"
        />
      )}
    </>
  ) : null;
};
